/**
 * Turns a recorded ActivityEvent into display copy.
 *
 * Nothing is invented: the title is the recorded event type with its
 * underscores removed, the summary is built only from values the backend
 * stored in the message, and anything technical (hashes, ids, versions)
 * is kept intact in `details` for the disclosure panel.
 */

export function humanize(value) {
  if (value === null || value === undefined || value === '') return '—';
  return String(value)
    .toLowerCase()
    .split('_')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');
}

/** Some events store a JSON object as their message. */
export function parseEventPayload(message) {
  if (typeof message !== 'string') return null;
  const trimmed = message.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

// Repeat what the row or page already shows; never worth a second line.
const REDUNDANT_KEYS = new Set(['actor_org_id', 'actor_user_id', 'package_id', 'correlation_id']);

// Verifiable but not readable: shown only under "Technical details".
const TECHNICAL_KEY = /(_hash$|_id$|_key$|^auth_context|_version$)/;

const SOURCE_LABEL = {
  USER_CONFIRMATION: 'confirmed by the originator',
  SYSTEM: 'set by the system',
  SYSTEM_INFERENCE: 'inferred by the system',
  PROVIDER: 'set by the provider',
};

function valueLabel(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  return /^[A-Z0-9_]+$/.test(text) ? humanize(text) : text;
}

function shortId(value) {
  const text = String(value || '');
  return text.length > 12 ? `${text.slice(0, 8)}…` : text;
}

function factConfirmedSummary(payload) {
  const fact = humanize(payload.fact_code);
  const next = valueLabel(payload.new_value);
  const prior = valueLabel(payload.prior_value);
  let sentence;
  if (next && prior) sentence = `${fact} changed from ${prior} to ${next}`;
  else if (next) sentence = `${fact} set to ${next}`;
  else if (prior) sentence = `${fact} cleared (was ${prior})`;
  else sentence = `${fact} confirmed`;
  const source = SOURCE_LABEL[payload.source];
  if (source) sentence += `, ${source}`;
  if (payload.document_id) sentence += ` on document ${shortId(payload.document_id)}`;
  // ORIGINATOR_CONFIRMED only restates the source; anything else is worth showing.
  if (payload.reason_code && payload.reason_code !== 'ORIGINATOR_CONFIRMED') {
    sentence += ` (${humanize(payload.reason_code)})`;
  }
  return `${sentence}.`;
}

function certifiedSummary(payload) {
  const version = payload.certification_version;
  return version ? `Package certified (version ${version}).` : 'Package certified.';
}

const PAYLOAD_SUMMARY = {
  ORIGINATOR_FACT_CONFIRMED: factConfirmedSummary,
  ORIGINATOR_PACKAGE_CERTIFIED: certifiedSummary,
};

function genericPayloadSummary(payload) {
  const parts = Object.entries(payload)
    .filter(([key]) => !REDUNDANT_KEYS.has(key) && !TECHNICAL_KEY.test(key))
    .map(([key, value]) => {
      const label = valueLabel(value);
      return label ? `${humanize(key)}: ${label}` : null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function plainMessageSummary(message) {
  // "Admission readiness BLOCKED -> EVALUATING" → "Admission readiness: Blocked → Evaluating"
  const transition = message.match(/^(.*?)\s+([A-Z0-9_]+)\s*->\s*([A-Z0-9_]+)$/);
  if (transition) {
    return `${transition[1]}: ${humanize(transition[2])} → ${humanize(transition[3])}`;
  }
  // "Document <uuid> classified as LEASE/AUTHORITATIVE_CANDIDATE via USER_CONFIRMATION"
  const classified = message.match(/^Document (\S+) classified as ([A-Z0-9_]+)\/([A-Z0-9_]+) via ([A-Z0-9_]+)$/);
  if (classified) {
    return `Document ${shortId(classified[1])} classified as ${humanize(classified[2])} · ${humanize(
      classified[3]
    )} (${humanize(classified[4]).toLowerCase()})`;
  }
  return message;
}

/**
 * @returns {{ title: string, summary: string | null, details: Array<[string, string]> }}
 */
export function describeActivityEvent(event) {
  const title = humanize(event?.event_type);
  const message = typeof event?.message === 'string' ? event.message.trim() : '';
  if (!message) return { title, summary: null, details: [] };

  const payload = parseEventPayload(message);
  if (!payload) return { title, summary: plainMessageSummary(message), details: [] };

  const summarise = PAYLOAD_SUMMARY[event.event_type] || genericPayloadSummary;
  const details = Object.entries(payload)
    .filter(([key, value]) => !REDUNDANT_KEYS.has(key) && value !== null && value !== undefined && value !== '')
    .map(([key, value]) => [humanize(key), typeof value === 'object' ? JSON.stringify(value) : String(value)]);

  return { title, summary: summarise(payload), details };
}
